<script setup lang="ts">
import type { ReadonlyDeep } from 'type-fest';
import type { RequiredKeys } from '@/utils/types';
import type { BadgeColor } from '@/components/badges/KBadge.vue';
import type * as openapi from '@/helpers/api/types/openapiOverrides';

import { computed } from 'vue';

import KBadge from '@/components/badges/KBadge.vue';
import ZammadTicketDetailsDescList from '@/components/descLists/ZammadTicketDetailsDescList.vue';
import OpenServiceDeskIconLink from '@/components/iconLinks/OpenServiceDeskIconLink.vue';
import KInfoWrapper from '@/components/wrappers/KInfoWrapper.vue';
import * as commonConstants from '@/constants/common';

export interface Props {
  ticket?: ReadonlyDeep<
    RequiredKeys<
      Partial<
        Pick<
          openapi.GetZammadTickets_200,
          | 'id'
          | 'state'
          | 'is_rfc'
          | 'created_by'
          | 'created_at'
          | 'updated_by'
          | 'updated_at'
          | 'rh_job'
        >
      >
    >
  >;
}
const props = withDefaults(defineProps<Props>(), {
  ticket: undefined,
});

const ticketState = computed(() => {
  if (!props.ticket?.state) return;
  return (
    props.ticket.state.charAt(0).toUpperCase() + props.ticket.state.slice(1)
  );
});

const ticketStateColor = computed<BadgeColor | undefined>(() => {
  if (!props.ticket?.state) return undefined;
  if (props.ticket.state === 'closed') return 'tertiary';
  if (props.ticket.state === 'open') return 'info';
  return undefined;
});
</script>

<template>
  <div
    :class="$style.zammadTicketDetailsUnit"
    data-testid="zammadTicketDetailsUnit"
  >
    <KInfoWrapper>
      <template #header>
        <template v-if="!props.ticket?.id">
          <h4 class="m-0">Ticket</h4>
        </template>
        <template v-else>
          <h4 class="d-inline-block m-0"> Ticket #{{ props.ticket.id }} </h4>
          <div :class="$style.buttons">
            <OpenServiceDeskIconLink
              :href="commonConstants.TICKET_SERVICE_PART_URL + props.ticket.id"
              target="_blank"
            />
            <KBadge
              v-if="props.ticket.is_rfc"
              content="RFC"
              inline
              color="warning"
            />
            <KBadge
              :content="ticketState || commonConstants.EM_DASH"
              inline
              :color="ticketStateColor"
            />
          </div>
        </template>
      </template>
      <ZammadTicketDetailsDescList :ticket="props.ticket" />
    </KInfoWrapper>
  </div>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

.zammadTicketDetailsUnit {
}
  .buttons {
    margin: -5px 0;
    display: flex;
    align-items: center;
  }
</style>
